import React from 'react';

import { Box, Divider, TableCell, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { toKebabCase } from '@uplift/utils';
import { Loader, ScrollableView } from '@uplift/ui-v2';
import { AgreementTermType, CollaborativeAgreementSigned } from '@uplift/api';
import { useTimezone } from '@uplift/contexts';
import { CalendarDateService } from '@uplift/services';
import { DateFormats } from '@uplift/constants';


export const DetailsCell: React.FC<{ label: string }> = ({ label, children }) => {
  return (
    <TableCell sx={{ px: 0, width: '50%' }}>
      <Typography variant="body2">{label}</Typography>
      <Typography variant="body1" color="textPrimary">{children}</Typography>
    </TableCell>
  );
};


export const EffectiveDateCell: React.VFC<{ document: CollaborativeAgreementSigned }> = ({ document }) => {
  const timezone = useTimezone();

  return (
    <DetailsCell label="Effective Date">
      {document.counterSignedAt
        ? CalendarDateService.date(document.counterSignedAt, DateFormats.ISO_DATE).tz(timezone).format('MMM D, YYYY')
        : 'Upon counter-signature'}
    </DetailsCell>
  );
};


export const agreementTermTypeLabelMap: Record<AgreementTermType, { many: string; single: string }> = {
  [AgreementTermType.days]: {
    many: 'Days',
    single: 'Day',
  },
  [AgreementTermType.months]: {
    many: 'Months',
    single: 'Month',
  },
  [AgreementTermType.years]: {
    many: 'Years',
    single: 'Year',
  },
};


const useStyles = makeStyles({ name: 'DocumentLayout' })(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    maxWidth: '930px',
    width: '100%',
    backgroundColor: theme.palette.common.white,
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontWeight: 500,
  },
  reviewSection: {
    maxWidth: 'min(calc(100% - 44px), 850px)',
    width: '100%',
    margin: '0 auto',
    padding: theme.spacing(2, 0),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(5, 0),
    }
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
  },
}));


export type DocumentLayoutProps = {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  contentSpacing?: boolean;
  controls?: React.ReactNode;
};


export const DocumentLayout: React.FC<DocumentLayoutProps> = ({ contentSpacing = true, controls, ...props }) => {
  const { classes } = useStyles();

  return (
    <Box className={classes.root}>
      <React.Suspense fallback={<Loader color="secondary" />}>
        <ScrollableView height="100%">
          <Box className={classes.reviewSection}>
            <React.Suspense fallback={<Loader color="secondary" />}>
              <Box className={classes.content}>
                <Box className={classes.title}>
                  <Typography data-testid={toKebabCase(props.title?.toString() || 'client-activity-title')}>{props.title}</Typography>
                  {props.subtitle && <Typography>{props.subtitle}</Typography>}
                </Box>
                <Box pt={2}>
                  <Divider />
                </Box>
                <Box pt={contentSpacing ? 2 : 0}>
                  {props.children}
                </Box>
              </Box>
            </React.Suspense>
          </Box>
        </ScrollableView>
        {controls}
      </React.Suspense>
    </Box>
  );
};
